import { Alert, AlertProvider } from './alert'
import { Avatar } from './avatar'
import { Badge } from './badge'
import Card from './card'
import {
  CodeEditor,
  CodeEditorV2,
  CodeEditorValidationResult,
  CodeEditorValidationSeverity,
} from './codeEditor'
import { CodeHighlight } from './codeHighlight'
import { ComboBox } from './combobox'
import { Command } from './command'
import {
  DatePicker,
  RangeDatePicker,
  RangeDatePickerQuickSelect,
  RawDatePicker,
} from './datePicker'
import {
  configurationHash,
  DittoConnectionValues,
  DittoIdentityType,
  DittoLogo,
} from './ditto'
import ErrorBoundary from './errorBoundary'
import { FileUploader } from './fileUploader'
import {
  CheckBox,
  DroppableTextArea,
  InputError,
  MultiSelect,
  PrettyRadioButtonGroup,
  RadioButtonList,
  Switch,
  ToggleButton,
} from './forms'
import {
  type Platform,
  useAnimatedSetState,
  useAnimationCycle,
  useBodyLock,
  useCopyToClipboard,
  useCurrentOrPrevious,
  useDetectTheme,
  useDevicePlatform,
  useLogSpan,
  useMediaMatch,
  usePopper,
  usePrevious,
  usePreviousIsDifferent,
  useScript,
  useSize,
} from './hooks'
import * as icons from './icons'
import Image from './image'
import {
  isSupportedJSONPickerType,
  JSONKeyPicker,
  SupportedJSONPickerType,
  toSupportedJSONPickerType,
} from './jsonKeyPicker'
import { Link, RawLink } from './link'
import { AnimatedList, AnimatedListItem } from './lists'
import { FullPageLoader, Loader } from './loader'
import MessagePanel from './messagePanel'
import { Popover } from './Popover'
import { ProgressBar } from './progressBar'
import { ScrollArea } from './scrollArea'
import { type SelectOption, RawSelect, Select } from './select'
import Separator from './separator'
import SlideOverPanel, { Sheet } from './slideOver'
import { IndeterminateSpinner, ProgressSpinner } from './spinner'
import {
  type ColumnFiltersState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableCellProps,
  type TableColumnDef,
  type TableRowProps,
  createColumnHelper,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  getCommonPinningStyles,
  renderUnknownValue,
  Table,
  TableActionsHeaderButton,
  TableCell,
  TablePageSelector,
  TablePageSizeSelector,
  TablePaginationControls,
  TableRow,
} from './table'
import { RadixTabs, Tab, Tabs } from './tabs'
import TextDownload from './textDownload'
import { Toaster } from './toast'
import { ToolBar, ToolBarButton } from './toolBar'
import { Tooltip } from './tooltip'
import { TreeItem, TreeView } from './treeView'
import { TwoColumn } from './twoColumn'
import {
  classes,
  noop,
  randomU64,
  stringify,
  truncate,
  truncateString,
  uuid,
  validations,
} from './utils'

export {
  Alert,
  AlertProvider,
  AnimatedList,
  AnimatedListItem,
  Avatar,
  Badge,
  Card,
  CheckBox,
  classes,
  CodeEditor,
  CodeEditorV2,
  CodeHighlight,
  ComboBox,
  Command,
  configurationHash,
  createColumnHelper,
  DatePicker,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  DittoLogo,
  DroppableTextArea,
  ErrorBoundary,
  FileUploader,
  FullPageLoader,
  getCommonPinningStyles,
  icons,
  Image,
  IndeterminateSpinner,
  InputError,
  isSupportedJSONPickerType,
  JSONKeyPicker,
  Link,
  Loader,
  MessagePanel,
  MultiSelect,
  noop,
  Popover,
  PrettyRadioButtonGroup,
  ProgressBar,
  ProgressSpinner,
  RadioButtonList,
  RadixTabs,
  randomU64,
  RangeDatePicker,
  RangeDatePickerQuickSelect,
  RawDatePicker,
  RawLink,
  RawSelect,
  renderUnknownValue,
  ScrollArea,
  Select,
  Separator,
  Sheet,
  SlideOverPanel,
  stringify,
  Switch,
  Tab,
  Table,
  TableActionsHeaderButton,
  TableCell,
  TablePageSelector,
  TablePageSizeSelector,
  TablePaginationControls,
  TableRow,
  Tabs,
  TextDownload,
  Toaster,
  ToggleButton,
  ToolBar,
  ToolBarButton,
  Tooltip,
  toSupportedJSONPickerType,
  TreeItem,
  TreeView,
  truncate,
  truncateString,
  TwoColumn,
  useAnimatedSetState,
  useAnimationCycle,
  useBodyLock,
  useCopyToClipboard,
  useCurrentOrPrevious,
  useDetectTheme,
  useDevicePlatform,
  useLogSpan,
  useMediaMatch,
  usePopper,
  usePrevious,
  usePreviousIsDifferent,
  useScript,
  useSize,
  uuid,
  validations,
}

export * from './button'
export * from './buttonGroup'
export * from './dialog'
export * from './dropdown'
export * from './form'
export * from './input'
export * from './label'
export * from './resizable'
export * from './typography'

export type {
  CodeEditorValidationResult,
  CodeEditorValidationSeverity,
  ColumnFiltersState,
  DittoConnectionValues,
  DittoIdentityType,
  PaginationState,
  Platform,
  RowSelectionState,
  SelectOption,
  SortingState,
  SupportedJSONPickerType,
  TableCellProps,
  TableColumnDef,
  TableRowProps,
}

/**
 * A utility type to simplify complex types to appease the type checker and avoid excessive
 * instantiation depth lints. This should be used sparingly and only when necessary.
 * Sourced from [`kysely`](https://kysely.dev/)
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type Simplify<T> = { [K in keyof T]: T[K] } & {}
